/* eslint-disable class-methods-use-this,max-classes-per-file */

'use strict';

define('vb/private/events/checkCancelEventBehavior',[
  'vb/private/events/eventBehavior',
  'vb/private/constants',
  'vb/private/log',
], (EventBehavior, Constants, Log) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/container').default} Page
   * @typedef {import('vb/private/events/eventModel').default} EventModel
   */

  const logger = Log.getLogger('/vb/private/events/checkCancelEventBehavior');

  /**
   * CheckCancelEventBehavior, behavior = "checkForCancel"
   */
  class CheckCancelEventBehavior extends EventBehavior {
    /**
     * Calls the (curried) invokeEvent functions serially, but stops when one returns a {"success", true} outcome.
     *
     * @param {Array<{ container: Container, fnc: function }>} functionWrappers - An array of objects where each object
     * contains a container and a function representing the event listener chain.
     * @returns {Promise<null|*>} A promise that resolves with the result of the invoked function
     * or null if event propagation is cancelled.
     * @protected
     * @overrides
     */
    executeInternal(functionWrappers) {
      const results = [];
      let cancelled = false;
      return functionWrappers
        .reduce((p, wrapper) => p
          .then((result) => {
            // if the previous one did not return a cancel result, keep going
            if (!cancelled) {
              if (!CheckCancelEventBehavior.isCancelResult(result)) {
                return Promise.resolve(wrapper.fnc(this))
                  .then((r) => {
                    results.push(r);
                    return r;
                  });
              }
              logger.info(`Event propagation cancelled for event ${this.name}`);
              cancelled = true;
            }
            return null;
          }),
        Promise.resolve(Constants.NO_EVENT_LISTENER_RESPONSE)); // @todo: dummy result for first invocation
    }

    /**
     * Determines if a given result represents an event cancellation.
     *
     * This method checks if the provided result is an array that contains at least one element
     * with a `result` object indicating a successful outcome with `stopPropagation` set to true.
     *
     * @param {*} result - The result to check, typically an array of result objects.
     * @returns {boolean} true if the result contains a cancellation indication, otherwise false.
     * @private
     */
    static isCancelResult(result) {
      return (Array.isArray(result)
        && result.some((wrapper) => wrapper
          && wrapper.result.name === 'success'
          && wrapper.result.result && wrapper.result.result.stopPropagation === true));
    }

    /**
     * Collects and curries event listeners, in a sequence where extensions receive the event first,
     * followed by the base.
     *
     * @param {Container|Page} container - The container in which the event listener is defined.
     * @param {EventModel} eventModel - The event model which contains metadata about the event.
     * @param {Object} eventWrapper - An object containing details about the event.
     * @param {String} eventWrapper.name - The name of the event.
     * @param {Container} eventWrapper.origin - The container that fired the event.
     * @param {*} eventWrapper.payload - The payload of the event.
     * @param {*} eventWrapper.coercedPayload - The payload coerced to the defined payload type.
     * @param {EventModel} eventWrapper.eventModel - The EventModel defining the event.
     * @returns {Array<{ container: Container|Page, fnc: function }>} An array of objects, where each object contains
     * a container and a curried event invocation function.
     * @protected
     * @overrides
     */
    curryListeners(container, eventModel, eventWrapper) {
      const functionWrappers = [];
      if (eventModel.isInterface) {
        const fncWrappers = this
          .recurseExtensionsAndCreateFunctions(container, eventWrapper);
        Array.prototype.push.apply(functionWrappers, fncWrappers);
      }

      const fnc = container.invokeEvent.bind(container, eventWrapper.name, eventWrapper.coercedPayload);
      functionWrappers.push({
        container,
        fnc,
      });

      return functionWrappers;
    }
  }

  return CheckCancelEventBehavior;
});

